import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiClient,
  Counterparty,
  DecaDataSnapshot,
  DecaDocument,
  DecaStatus,
  DecaTemplate,
  PaginatedResult,
  ThemePreference,
  Vehicle,
} from "@deca/shared-types";
import type { DecaFormPayload } from "../lib/deca-form";
import { useNavigate } from "react-router-dom";
import { useApi } from "../app/ApiProvider";
import { invoicesPollInterval, subscriptionPollInterval, subscriptionsPollInterval } from "../lib/billing-refresh";
import { useAuth } from "../app/AuthProvider";

/**
 * Tipos de lo que devuelve la API al panel, y los hooks de consulta.
 *
 * Se reutilizan los tipos de `@deca/shared-types` siempre que existen: si la
 * API cambia un campo, el error aparece al compilar el panel, no en pantalla.
 */

export interface MeResponse {
  type: "user";
  id: string;
  email: string;
  fullName: string;
  isSuperAdmin: boolean;
  themePreference: ThemePreference;
  preferredLanguage: string;
  pushNotificationsEnabled: boolean;
  mfaEnabled: boolean;
  pinEnabled?: boolean;
  pinMustChange?: boolean;
  /** Solo quien es únicamente conductor puede usar el PIN; el resto usa MFA. */
  canUsePin?: boolean;
  recoveryCodesRemaining?: number;
  tenantId: string | null;
  role: string | null;
}

export interface LoginResponse {
  accessToken: string;
  requiresTenantSelection: boolean;
  requiresMfa: boolean;
  secondFactor?: "totp" | "pin" | null;
  pinMustChange?: boolean;
  recoveryCodesRemaining?: number;
  availableTenants: Array<{ tenantId: string; tenantName: string; role: string }>;
}

export interface TenantSummary {
  id: string;
  name: string;
  cif: string | null;
  isActive: boolean;
  manuallyBlocked: boolean;
  /** Tipo fiscal asignado (`TaxType.id`). Solo lo devuelve el listado del super-admin. */
  taxTypeId?: string | null;
  /** Código de cliente del ERP. Solo lo devuelve el listado del super-admin. */
  erpCustomerCode?: string | null;
  role?: string;
}

export interface TenantsResponse {
  items: TenantSummary[];
  canCrossTenants: boolean;
}

export interface TenantDetail {
  id: string;
  name: string;
  cif: string;
  isActive: boolean;
  manuallyBlocked: boolean;
  manuallyBlockedReason: string | null;
  /** Días de trial configurados. null = usar defecto plataforma. 0 = sin trial. */
  trialDays: number | null;
  /** Correo de facturación (el que ve Stripe). null = se usa el del administrador. */
  billingEmail: string | null;
  /** Razón social para la factura. null = se usa el nombre del tenant. */
  legalName: string | null;
  billingAddress: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingProvince: string | null;
  /** País ISO de 2 letras. null = ES. */
  billingCountry: string | null;
  /** Código de cliente del ERP (único). */
  erpCustomerCode: string | null;
  /** Tipo fiscal (`TaxType.id`): su porcentaje se suma al neto de las facturas. null = sin impuesto. */
  taxTypeId: string | null;
  /** Almacén S3 asignado. null = hereda el por defecto de la plataforma. */
  s3ConfigId: string | null;
  /** Fecha de alta del tenant: el trial sin suscripción se cuenta desde aquí. */
  createdAt: string;
  counts: { users: number; vehicles: number; counterparties: number; templates: number; documents: number };
  subscription: {
    id: string;
    status: string;
    billingPlanId: string;
    billingPlanName: string;
    periodicity: string;
    trialEndsAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    autoRenew: boolean;
  } | null;
}

export interface CreatedTenant {
  id: string;
  name: string;
  cif: string;
  admin: {
    id: string;
    email: string;
    fullName: string;
    temporaryPassword: string | null;
    existingAccount: boolean;
  };
}

export interface UserSummary {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  role: string;
  tenantId: string;
  tenantName: string | null;
  counterpartyId: string | null;
  counterpartyName: string | null;
  mfaEnabled?: boolean;
  pinEnabled?: boolean;
  pinMustChange?: boolean;
}

export interface InvitedUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  temporaryPassword: string | null;
  existingAccount: boolean;
}

export interface AuditEntry {
  id: string;
  tenantId: string;
  actedAsTenantId: string | null;
  actorType: string;
  actorUserId: string | null;
  actorApiClientId: string | null;
  decaDocumentId: string | null;
  action: string;
  changesJson: Record<string, unknown> | null;
  actorIsSuperAdmin: boolean;
  createdAt: string;
  /** Clave S3 del PDF huérfano (present cuando la firma o el documento se borró pero S3 falló) */
  orphanedS3Key: string | null;
  /** Fecha estimada de expiración del Object Lock (uploadedAt + 366 días) */
  estimatedLockExpiry: string | null;
  /** true cuando el super-admin eliminó el objeto S3 manualmente */
  s3Cleaned: boolean | null;
}

export interface DocumentChainEntry {
  decaDocumentId: string;
  urlToken: string;
  status: string;
  createdAt: string;
  relatedDocumentId: string | null;
  isCurrent: boolean;
  versions: Array<{
    id: string;
    versionNumber: number;
    createdAt: string;
    changeReason: string | null;
    /** `true` en la versión que hoy sirven el PDF y el QR. */
    isCurrent: boolean;
    /** Qué cambió respecto a la versión anterior (vacío en la v1). */
    changes: string[];
  }>;
}

export interface DecaDetail {
  document: DecaDocument;
  /** Snapshot congelado de la versión vigente: partes, envíos y observaciones. */
  dataSnapshot: DecaDataSnapshot | null;
  chain: DocumentChainEntry[];
}

export interface SignatureRow {
  id: string;
  decaVersionId: string;
  signerRole: string;
  signerName: string;
  signedAt: string;
  sha256Hash: string;
  signatureMethod: string;
  hashFromPdf: boolean;
}

export interface ReportRow {
  id: string;
  tenantId: string;
  urlToken: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  versionsCount: number;
  relatedDocumentId: string | null;
  hasCorrections: boolean;
  hasIncidents: boolean;
  incidentNotes: string | null;
  shipmentCount: number;
  cargadorContractual: string | null;
  transportistaEfectivo: string | null;
}

export interface ReportResult {
  rows: ReportRow[];
  total: number;
  summary: { withCorrections: number; withIncidents: number; byStatus: Record<string, number> };
}

export interface CreatedApiKey extends ApiClient {
  secret: string;
}

export interface DecaListFilters {
  status?: string;
  hasIncidents?: boolean;
  /** true = solo cerrados manualmente; false = solo no cerrados. undefined = todos. */
  isClosed?: boolean;
  dateFrom?: string;
  dateTo?: string;
  page: number;
  pageSize: number;
}

export interface ReportFilters {
  dateFrom?: string;
  dateTo?: string;
  tenantId?: string;
  authorUserId?: string;
  status?: string;
  hasCorrections?: boolean;
  hasIncidents?: boolean;
}

/**
 * Claves de caché jerárquicas: invalidar `["deca"]` refresca listado y
 * detalle, sin tener que enumerar cada clave a mano en cada mutación.
 */
export const queryKeys = {
  // Claves globales de plataforma: no dependen del tenant activo.
  me: ["me"] as const,
  tenants: ["tenants"] as const,
  branding: ["branding"] as const,
  billingPlans: ["billing-plans"] as const,
  taxTypes: ["tax-types"] as const,
  platformStats: ["platform-stats"] as const,
  subscriptions: (filters?: SubscriptionOverviewFilters) => ["subscriptions", filters ?? {}] as const,

  // Claves por-tenant: incluyen tenantId para que React Query trate cada
  // tenant como una entrada de caché independiente. El tenantId en la key
  // es la segunda defensa (la primera es el key={activeTenantId} en
  // AuthenticatedApp que fuerza remount al cambiar tenant); garantiza que
  // si el superAdmin vuelve a un tenant anterior, se hace un fetch nuevo en
  // lugar de servir datos cacheados de otro tenant con la misma key.
  users: (tenantId?: string | null) => ["users", tenantId ?? "current"] as const,
  counterparties: (tenantId: string | null) => ["counterparties", tenantId] as const,
  vehicles: (tenantId: string | null) => ["vehicles", tenantId] as const,
  templates: (tenantId: string | null) => ["templates", tenantId] as const,
  apiClients: (tenantId: string | null) => ["api-clients", tenantId] as const,
  deca: (tenantId: string | null, filters?: DecaListFilters) => ["deca", tenantId, filters ?? {}] as const,
  decaDetail: (tenantId: string | null, id: string) => ["deca", tenantId, "detail", id] as const,
  signatures: (tenantId: string | null, id: string) => ["deca", tenantId, "signatures", id] as const,
  audit: (tenantId: string | null, filters: Record<string, unknown>) => ["audit", tenantId, filters] as const,
  reports: (tenantId: string | null, filters: ReportFilters) => ["reports", tenantId, filters] as const,
  tenantSubscription: (tenantId: string | null) => ["tenant-subscription", tenantId] as const,
  tenantInvoices: (tenantId: string | null, filters?: { page?: number; pageSize?: number }) =>
    ["tenant-invoices", tenantId, filters ?? {}] as const,
  notifications: (tenantId: string | null) => ["notifications", tenantId] as const,
};

/** Invalida todo lo que cambia al emitir o modificar un DeCA. */
function useInvalidateDeca(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["deca"] }),
      client.invalidateQueries({ queryKey: ["reports"] }),
      client.invalidateQueries({ queryKey: ["audit"] }),
    ]);
  };
}

// ------------------------------------------------------------
// Sesión
// ------------------------------------------------------------

export function useMe(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<MeResponse>("/api/v1/me"),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export function useTenants(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.tenants,
    queryFn: () => api.get<TenantsResponse>("/api/v1/internal/tenants"),
    enabled,
    retry: false,
  });
}

// ------------------------------------------------------------
// Gestión de tenants (solo super-admin)
// ------------------------------------------------------------

export function useTenantDetail(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["tenants", "detail", id ?? "none"] as const,
    queryFn: () => api.get<TenantDetail>(`/api/v1/internal/tenants/${id}`),
    enabled: Boolean(id),
    // La ficha muestra el estado de la suscripción: se refresca igual que en Suscripciones.
    refetchOnWindowFocus: "always",
    refetchInterval: (query) => subscriptionPollInterval(query.state.data?.subscription),
  });
}

export function useCreateTenant() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: {
      name: string;
      cif: string;
      adminEmail: string;
      adminFullName: string;
      billingEmail?: string;
      taxTypeId?: string;
      legalName?: string;
      billingAddress?: string;
      billingPostalCode?: string;
      billingCity?: string;
      billingProvince?: string;
      billingCountry?: string;
      erpCustomerCode?: string;
      billingPlanId?: string;
      periodicity: string;
      trialDays?: number;
    }) =>
      api.post<CreatedTenant>("/api/v1/internal/tenants", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.tenants });
    },
  });
}

export function useUpdateTenant() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Record<string, unknown> }) =>
      api.patch<TenantDetail>(`/api/v1/internal/tenants/${id}`, values),
    onSuccess: async (_result, variables) => {
      await client.invalidateQueries({ queryKey: queryKeys.tenants });
      await client.invalidateQueries({ queryKey: ["tenants", "detail", variables.id] });
    },
  });
}

export function useUsers(tenantId?: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.users(tenantId),
    queryFn: () =>
      api.get<{ items: UserSummary[] }>("/api/v1/internal/users", {
        query: tenantId ? { tenantId } : {},
      }),
    // Sin tenantId la query siempre da 400: no la lanzamos hasta tenerlo.
    enabled: tenantId != null && tenantId !== "",
    staleTime: 60_000,
  });
}

export function useInviteUser() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: { email: string; fullName: string; role: string }) =>
      api.post<InvitedUser>("/api/v1/internal/users", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useUpdateUserRole() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch<{ userId: string; role: string }>(`/api/v1/internal/users/${id}/role`, { role }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRevokeUserAccess() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/users/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export interface UpdateUserProfileValues {
  fullName?: string;
  email?: string;
  isActive?: boolean;
  counterpartyId?: string | null;
}

export function useUpdateUserProfile() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: UpdateUserProfileValues }) =>
      api.patch<UserSummary>(`/api/v1/internal/users/${id}`, values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useResetUserPassword() {
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ temporaryPassword: string }>(`/api/v1/internal/users/${id}/reset-password`),
  });
}

/** Quita el MFA o el PIN de otra persona (super-admin: cualquiera; ADMIN: su empresa, salvo otros ADMIN). */
export function useResetUserSecondFactor() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ mfaEnabled: false; pinEnabled: false }>(`/api/v1/internal/users/${id}/mfa/reset`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** Genera un PIN temporal (6 dígitos, 72 h) para un conductor. Se devuelve en claro una sola vez. */
export function useGenerateDriverPin() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ temporaryPin: string; expiresAt: string }>(`/api/v1/internal/users/${id}/pin/generate`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["users"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ------------------------------------------------------------
// Seguridad de la cuenta propia: MFA (TOTP), códigos de recuperación y PIN
// ------------------------------------------------------------

export function useMfaSetup() {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.post<{ secret: string; otpauthUrl: string }>("/api/v1/auth/mfa/setup"),
  });
}

/** Estas acciones piden un código/contraseña y responden 401 si están mal: no es una sesión caída. */
const KEEP_SESSION = { keepSessionOn401: true } as const;

export function useMfaEnable() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { secret: string; code: string }) =>
      api.post<{ mfaEnabled: true; recoveryCodes: string[] }>("/api/v1/auth/mfa/enable", input, KEEP_SESSION),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useMfaDisable() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string }) => api.post<{ mfaEnabled: false }>("/api/v1/auth/mfa/disable", input, KEEP_SESSION),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useRegenerateRecoveryCodes() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string }) =>
      api.post<{ recoveryCodes: string[] }>("/api/v1/auth/mfa/recovery-codes/regenerate", input, KEEP_SESSION),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useSetPin() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { pin: string; password: string }) =>
      api.put<{ pinEnabled: true; pinMustChange: false }>("/api/v1/me/pin", input, KEEP_SESSION),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useDisablePin() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { password: string }) => api.post<{ pinEnabled: false }>("/api/v1/me/pin/disable", input, KEEP_SESSION),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

// ------------------------------------------------------------
// Catálogos
// ------------------------------------------------------------

export function useCounterparties(params: { defaultRole?: string; search?: string } = {}) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.counterparties(activeTenantId), params] as const,
    queryFn: () => api.get<{ items: Counterparty[] }>("/api/v1/internal/counterparties", { query: params }),
  });
}

export function useVehicles(params: { search?: string } = {}) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.vehicles(activeTenantId), params] as const,
    queryFn: () => api.get<{ items: Vehicle[] }>("/api/v1/internal/vehicles", { query: params }),
  });
}

export function useSaveCounterparty() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id
        ? api.patch<Counterparty>(`/api/v1/internal/counterparties/${id}`, values)
        : api.post<Counterparty>("/api/v1/internal/counterparties", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["counterparties"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteCounterparty() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/counterparties/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["counterparties"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useSaveVehicle() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id
        ? api.patch<Vehicle>(`/api/v1/internal/vehicles/${id}`, values)
        : api.post<Vehicle>("/api/v1/internal/vehicles", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vehicles"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ------------------------------------------------------------
// Plantillas de marca
// ------------------------------------------------------------

export function useTemplates() {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.templates(activeTenantId),
    queryFn: () => api.get<{ items: DecaTemplate[] }>("/api/v1/internal/templates"),
  });
}

export function useCreateTemplate() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.post<DecaTemplate>("/api/v1/internal/templates", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useUpdateTemplate() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Record<string, unknown> }) =>
      api.patch<DecaTemplate>(`/api/v1/internal/templates/${id}`, values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["templates"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteTemplate() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/templates/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["templates"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useUploadLogo() {
  const api = useApi();
  return useMutation({
    mutationFn: (values: { filename: string; contentType: string; base64: string }) =>
      api.post<{ logoS3Key: string; sizeBytes: number }>("/api/v1/internal/templates/logo", values),
  });
}

/** Vista previa: devuelve un Blob PDF con el mismo generador que emite los DeCA. */
export function useTemplatePreview() {
  const api = useApi();
  return useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.post<Blob>("/api/v1/internal/templates/preview", values, { responseType: "blob" }),
  });
}

// ------------------------------------------------------------
// Marca de la aplicación (icono/logo del panel y la app móvil)
// ------------------------------------------------------------
// Distinto de `useTemplates`/`useUploadLogo`: aquello es el logo que va
// DENTRO del PDF del DeCA, por tenant. Esto es el icono de la app en sí,
// visible en la pantalla de login (sin sesión) y en la cabecera del panel
// (con sesión) — una única fila de configuración, global a la plataforma.

// Tres variantes: `icon` (app móvil), `wordmarkLight` (panel, fondo claro,
// p.ej. login) y `wordmarkDark` (panel, fondo oscuro, p.ej. barra lateral).
export type PlatformLogoVariant = "icon" | "wordmarkLight" | "wordmarkDark";

export interface BrandingResponse {
  icon: string | null;
  wordmarkLight: string | null;
  wordmarkDark: string | null;
  /** Nombre de fallback cuando no hay wordmark subido. `null` = usar "DeKA". */
  appName: string | null;
}

/** Se usa tanto antes de iniciar sesión (login) como después (sidebar). */
export function useBranding() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.branding,
    queryFn: () => api.get<BrandingResponse>("/api/v1/branding"),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useUploadPlatformLogo() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: {
      variant: PlatformLogoVariant;
      filename: string;
      contentType: string;
      base64: string;
    }) =>
      api.post<{ variant: PlatformLogoVariant; logoS3Key: string; sizeBytes: number }>(
        "/api/v1/internal/branding/logo",
        values,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.branding });
    },
  });
}

/**
 * Nombre de marca visible cuando no hay wordmark subido. No afecta al
 * nombre nativo de la app móvil (app.json): ese requiere recompilar y
 * republicar el binario, no se puede cambiar en caliente.
 */
export function useUpdatePlatformAppName() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (appName: string) =>
      api.patch<{ appName: string }>("/api/v1/internal/branding/name", { appName }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.branding });
    },
  });
}

// ------------------------------------------------------------
// Claves de API
// ------------------------------------------------------------

export function useApiClients() {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.apiClients(activeTenantId),
    queryFn: () => api.get<{ items: ApiClient[] }>("/api/v1/internal/api-clients"),
  });
}

export function useCreateApiClient() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: { name: string; scopes: string[]; webhookUrl?: string | null }) =>
      api.post<CreatedApiKey>("/api/v1/internal/api-clients", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["api-clients"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRevokeApiClient() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/api-clients/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["api-clients"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ------------------------------------------------------------
// DeCA
// ------------------------------------------------------------

export function useDecaList(filters: DecaListFilters) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.deca(activeTenantId, filters),
    queryFn: () =>
      api.get<PaginatedResult<DecaDocument>>("/api/v1/internal/deca", {
        query: {
          page: filters.page,
          pageSize: filters.pageSize,
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.hasIncidents !== undefined ? { hasIncidents: filters.hasIncidents } : {}),
          ...(filters.isClosed !== undefined ? { isClosed: filters.isClosed } : {}),
          ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
          ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useDecaDetail(id: string | null) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.decaDetail(activeTenantId, id ?? "none"),
    queryFn: () => api.get<DecaDetail>(`/api/v1/internal/deca/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateDeca() {
  const api = useApi();
  const invalidate = useInvalidateDeca();
  return useMutation({
    mutationFn: (payload: DecaFormPayload) =>
      api.post<{ id: string; urlToken: string; publicUrl: string; pdfSizeBytes: number }>(
        "/api/v1/internal/deca",
        payload,
        // Reintentar sin clave duplicaría el documento: con ella, el servidor
        // devuelve el mismo resultado.
        { idempotencyKey: crypto.randomUUID() },
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateIncident() {
  const api = useApi();
  const invalidate = useInvalidateDeca();
  return useMutation({
    mutationFn: ({ id, hasIncidents, incidentNotes }: { id: string; hasIncidents: boolean; incidentNotes: string | null }) =>
      api.patch<DecaDocument>(`/api/v1/internal/deca/${id}/incident`, { hasIncidents, incidentNotes }),
    onSuccess: invalidate,
  });
}

export function useCloseDeca() {
  const api = useApi();
  const invalidate = useInvalidateDeca();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: string; closedAt: string }>(`/api/v1/internal/deca/${id}/close`, {}),
    onSuccess: invalidate,
  });
}

export function useReopenDeca() {
  const api = useApi();
  const invalidate = useInvalidateDeca();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: string; closedAt: string | null }>(`/api/v1/internal/deca/${id}/reopen`, {}),
    onSuccess: invalidate,
  });
}

// ── Bloque 2: hooks de borrado ────────────────────────────────────────────────

export function useDeactivateVehicle() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ id: string; isActive: boolean }>(`/api/v1/internal/vehicles/${id}/deactivate`, {}),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vehicles"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useReactivateVehicle() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ id: string; isActive: boolean }>(`/api/v1/internal/vehicles/${id}/reactivate`, {}),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vehicles"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/** Baja lógica (`deactivate`) o recuperación (`reactivate`) de una contraparte. */
export function useToggleCounterparty(action: "deactivate" | "reactivate") {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ id: string; isActive: boolean }>(`/api/v1/internal/counterparties/${id}/${action}`, {}),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["counterparties"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteVehicle() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/vehicles/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vehicles"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteDecaSignature() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, signatureId }: { documentId: string; signatureId: string }) =>
      api.delete<{ orphanedKeys?: string[] }>(
        `/api/v1/internal/deca/${documentId}/signature/${signatureId}`,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["deca"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useDeleteDeca() {
  const api = useApi();
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ orphanedKeys?: string[] }>(`/api/v1/internal/deca/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["deca"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
      navigate("/deca");
    },
  });
}

/**
 * Lista los usuarios con rol CONDUCTOR del tenant activo.
 * Reutiliza el endpoint `/api/v1/internal/users` filtrando por rol en cliente,
 * ya que el backend no expone un filtro de rol en la query de usuarios.
 */
export function useDrivers() {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: ["users", "drivers", activeTenantId],
    queryFn: () =>
      api
        .get<{ items: UserSummary[] }>("/api/v1/internal/users", {
          query: activeTenantId ? { tenantId: activeTenantId } : {},
        })
        .then((r) => r.items.filter((u) => u.role === "CONDUCTOR" && u.isActive)),
    enabled: Boolean(activeTenantId),
    staleTime: 60_000,
  });
}

export interface UpdateDecaAssignmentsInput {
  driverId?: string | null;
  vehiculo?: { vehicleId: string } | { detail: { plateTractor: string; plateTrailer?: string | null } } | null;
  operadorIntermedio?:
    | { counterpartyId: string }
    | { detail: { name: string; nif: string; address?: string | null } }
    | null;
  /** Array paralelo a los envíos: índice i = envío i. null limpia el destinatario. */
  destinatarios?: Array<
    | { counterpartyId: string }
    | { detail: { name: string; nif: string; address?: string | null } }
    | null
    | undefined
  > | null;
  changeReason?: string;
}

export interface UpdateDecaAssignmentsResult {
  id: string;
  mode: "driver_only" | "same_pdf" | "new_pdf";
  driverId?: string | null;
  urlToken?: string;
  publicUrl?: string;
  versionNumber?: number;
  pdfSizeBytes?: number;
}

/**
 * Reasigna campos opcionales del DeCA (conductor, vehículo, operador
 * intermedio, destinatario) sin necesidad de reemplazar todos los datos.
 *
 * - Solo `driverId` → actualiza la FK en BD sin nueva versión PDF.
 * - `vehiculo` / `operadorIntermedio` / `destinatarios` → nueva versión
 *   en modo `same_pdf` (misma URL/QR, nuevo PDF actualizado).
 */
export function useUpdateDecaAssignments() {
  const api = useApi();
  const invalidate = useInvalidateDeca();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateDecaAssignmentsInput) =>
      api.patch<UpdateDecaAssignmentsResult>(`/api/v1/internal/deca/${id}/assignments`, body),
    onSuccess: invalidate,
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export function useSignatures(documentId: string | null) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.signatures(activeTenantId, documentId ?? "none"),
    queryFn: () => api.get<{ items: SignatureRow[] }>(`/api/v1/internal/deca/${documentId}/signatures`),
    enabled: Boolean(documentId),
  });
}

export function useCreateSignature() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...values
    }: {
      id: string;
      signerRole: string;
      signerName: string;
      signatureMethod: string;
    }) => api.post<SignatureRow>(`/api/v1/internal/deca/${id}/signature`, values),
    onSuccess: async (_result, variables) => {
      // Refrescar la lista de firmas y el detalle del documento usando prefijo
      // base — así invalida independientemente del tenantId en la queryKey.
      await client.invalidateQueries({ queryKey: ["deca"] });
      await client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

/**
 * Descarga el PDF del DeCA como Blob y devuelve una blob: URL local.
 *
 * El endpoint `/api/v1/internal/deca/:id/pdf` requiere `Authorization: Bearer`
 * — un iframe apuntando directamente a esa URL fallaría con 401 porque el
 * navegador no adjunta headers personalizados en las peticiones del iframe.
 *
 * Solución: el hook usa el cliente API (con token) para descargar el binario,
 * crea una `blob: URL` efímera y la devuelve. El iframe y `window.open` abren
 * esa URL local sin ninguna petición adicional.
 *
 * La blob URL se revoca automáticamente en la siguiente llamada para evitar
 * fugas de memoria.
 */
export function usePdfUrl(documentId: string) {
  const api = useApi();
  // Guardamos la blob URL anterior para revocarla antes de crear una nueva.
  let previousBlobUrl: string | null = null;

  return useMutation({
    mutationFn: async () => {
      // Revocar la blob URL previa antes de crear otra.
      if (previousBlobUrl) {
        URL.revokeObjectURL(previousBlobUrl);
        previousBlobUrl = null;
      }
      // Descargamos el PDF como Blob usando el cliente autenticado.
      const blob = await api.get<Blob>(`/api/v1/internal/deca/${documentId}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      previousBlobUrl = url;
      return { url, expiresInSeconds: undefined };
    },
  });
}

/**
 * Abre el PDF de una versión concreta del historial (p. ej. la v1 tras una
 * reasignación). Misma mecánica que `usePdfUrl`: el binario se pide con el
 * cliente autenticado y se devuelve como blob URL, porque una pestaña nueva no
 * enviaría la cabecera Authorization.
 */
export function useVersionPdf() {
  const api = useApi();
  return useMutation({
    mutationFn: async (args: { documentId: string; versionId: string }) => {
      const blob = await api.get<Blob>(
        `/api/v1/internal/deca/${args.documentId}/versions/${args.versionId}/pdf`,
        { responseType: "blob" },
      );
      return { url: URL.createObjectURL(blob) };
    },
  });
}

// ------------------------------------------------------------
// Auditoría y reportes
// ------------------------------------------------------------

export function useAuditLog(filters: {
  page: number;
  pageSize: number;
  action?: string;
  actorUserId?: string;
  decaDocumentId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const api = useApi();
  // activeTenantId en la queryKey garantiza que al cambiar de tenant React
  // Query trate esto como una query distinta y haga un fetch nuevo, incluso
  // aunque los filtros no hayan cambiado (AppLayout nunca se desmonta).
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.audit(activeTenantId, filters as unknown as Record<string, unknown>),
    queryFn: () => api.get<PaginatedResult<AuditEntry>>("/api/v1/internal/audit-log", { query: filters }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Entradas de auditoría con clave S3 huérfana pendiente de limpieza.
 * Solo útil para super-admin en modo plataforma (sin tenant activo).
 */
export function useOrphanedS3Log(filters: { page: number; pageSize: number }) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: ["audit", "orphaned", activeTenantId, filters],
    queryFn: () =>
      api.get<PaginatedResult<AuditEntry>>("/api/v1/internal/audit-log", {
        query: { ...filters, hasOrphanedKey: "true" },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useDecaReport(filters: ReportFilters) {
  const api = useApi();
  // activeTenantId en la queryKey garantiza que al cambiar de tenant React
  // Query trate esto como una query distinta y haga un fetch nuevo, incluso
  // aunque los filtros no hayan cambiado (AppLayout nunca se desmonta).
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.reports(activeTenantId, filters),
    queryFn: () =>
      api.get<ReportResult>("/api/v1/internal/reports/deca", {
        query: filters as Record<string, string | number | boolean | undefined>,
      }),
  });
}

/** Descarga el reporte (CSV o Excel) respetando los mismos filtros. */
export function useExportReport() {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ format, filters }: { format: "csv" | "xlsx"; filters: ReportFilters }) => {
      const blob = await api.get<Blob>("/api/v1/internal/reports/deca/export", {
        query: { ...filters, format } as Record<string, string | number | boolean | undefined>,
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `reporte-deca.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },
  });
}

// ------------------------------------------------------------
// Preferencias
// ------------------------------------------------------------

export function useUpdateThemePreference() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (themePreference: ThemePreference) =>
      api.patch<{ themePreference: ThemePreference }>("/api/v1/internal/me/theme", { themePreference }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useUpdateLanguagePreference() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (preferredLanguage: string) =>
      api.patch<{ preferredLanguage: string }>("/api/v1/internal/me/language", { preferredLanguage }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useUpdatePushPreference() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (pushNotificationsEnabled: boolean) =>
      api.patch<{ pushNotificationsEnabled: boolean }>("/api/v1/internal/me/push-notifications", {
        pushNotificationsEnabled,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ------------------------------------------------------------
// Facturación — planes de facturación (super-admin)
// ------------------------------------------------------------

export interface BillingPlan {
  id: string;
  name: string;
  basePrice: number;
  pricePerDriver: number;
  pricePerCargador: number;
  pricePerDeca: number;
  /** null = ilimitado */
  maxDecasPerMonth: number | null;
  currency: string;
  isActive: boolean;
  /** Suscripciones activas (TRIAL + ACTIVE) que usan este plan. */
  subscriberCount: number;
}

// ------------------------------------------------------------
// Facturación — tipos fiscales (super-admin)
// ------------------------------------------------------------

export interface TaxType {
  id: string;
  name: string;
  /** Lo que ve el cliente en su factura: IVA, IGIC, IPSI... */
  label: string;
  /** Porcentaje que se SUMA al neto (Prisma Decimal llega como string). */
  percentage: number | string;
  /** Mención legal que lleva la factura con este tipo (null = ninguna). */
  legalNote: string | null;
  isDefault: boolean;
  isActive: boolean;
  /** Tenants que lo tienen asignado. */
  tenantCount: number;
}

export interface TaxTypeSyncResult {
  total: number;
  failed: number;
}

export interface TaxTypeValues {
  name: string;
  label: string;
  percentage: number;
  /** null = sin mención. */
  legalNote: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export function useTaxTypes() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.taxTypes,
    queryFn: () => api.get<{ items: TaxType[] }>("/api/v1/internal/tax-types").then((r) => r.items),
  });
}

export function useCreateTaxType() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: TaxTypeValues) => api.post<TaxType>("/api/v1/internal/tax-types", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.taxTypes });
    },
  });
}

export function useUpdateTaxType() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<TaxTypeValues> }) =>
      api.patch<TaxType & { sync: TaxTypeSyncResult }>(`/api/v1/internal/tax-types/${id}`, values),
    onSuccess: async () => {
      // Cambiar el predeterminado toca otras filas: se recarga la lista entera.
      await client.invalidateQueries({ queryKey: queryKeys.taxTypes });
    },
  });
}

export interface SubscriptionOverviewFilters {
  status?: string;
  search?: string;
}

export interface SubscriptionOverview {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantCif: string | null;
  /** null cuando el tenant no tiene plan asignado (status === "SIN_PLAN"). */
  billingPlanId: string | null;
  billingPlanName: string | null;
  periodicity: string | null;
  /** "SIN_PLAN" para tenants sin suscripción; resto son valores de SubscriptionStatus. */
  status: string;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  autoRenew: boolean | null;
  hasPaymentMethodOnFile: boolean | null;
  blockedAt: string | null;
}

// ---------------------------------------------------------------------------
// Estadísticas globales de plataforma (solo super-admin)
// ---------------------------------------------------------------------------

export interface PlatformStats {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  activeSubscriptions: number;
  tenantsWithoutPlan: number;
  totalUsers: number;
  totalDocuments: number;
  documentsLast30Days: number;
  subscriptionsByStatus: Record<string, number>;
}

export function usePlatformStats() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.platformStats,
    queryFn: () => api.get<PlatformStats>("/api/v1/internal/platform-stats"),
  });
}

export function useBillingPlans() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.billingPlans,
    // La API devuelve { items: BillingPlan[] } (formato paginado sin cursor),
    // no un array directo. Se extrae .items para que los consumidores del hook
    // reciban BillingPlan[] directamente y puedan llamar a .filter() sin crash.
    queryFn: () =>
      api
        .get<{ items: BillingPlan[] }>("/api/v1/internal/billing-plans")
        .then((r) => r.items),
  });
}

export function useCreateBillingPlan() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: {
      name: string;
      basePrice: number;
      pricePerDriver: number;
      pricePerCargador: number;
      pricePerDeca: number;
      maxDecasPerMonth: number | null;
      currency: string;
      isActive: boolean;
    }) => api.post<BillingPlan>("/api/v1/internal/billing-plans", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

export function useUpdateBillingPlan() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<Omit<BillingPlan, "id">> }) =>
      api.patch<BillingPlan>(`/api/v1/internal/billing-plans/${id}`, values),
    onSuccess: async (updated) => {
      // Actualizar el item en el caché directamente con la respuesta del servidor
      // para que la tabla muestre el valor correcto sin esperar un refetch
      client.setQueryData<BillingPlan[]>(queryKeys.billingPlans, (old) =>
        old ? old.map((p) => (p.id === updated.id ? updated : p)) : old,
      );
      await client.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

export interface ApplyBillingPlanTenantResult {
  tenantId: string;
  tenantName: string | null;
  outcome: "applied" | "unchanged" | "error";
  changedComponents: Array<"base" | "driver" | "cargador" | "deca_usage">;
  usageCleared: boolean;
  metadataUpdated: boolean;
  error?: string;
}

export interface ApplyBillingPlanResult {
  planId: string;
  planName: string;
  total: number;
  applied: number;
  unchanged: number;
  failed: number;
  results: ApplyBillingPlanTenantResult[];
}

/** Lleva a Stripe los valores actuales de un plan para todas las suscripciones que lo usan. */
export function useApplyBillingPlan() {
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.post<ApplyBillingPlanResult>(`/api/v1/internal/billing-plans/${id}/apply`, {}),
  });
}

/** Toggle rápido activo/inactivo de un plan sin abrir el modal de edición. */
export function useToggleBillingPlanActive() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<BillingPlan>(`/api/v1/internal/billing-plans/${id}`, { isActive }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

export function useSubscriptionsOverview(filters: SubscriptionOverviewFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.subscriptions(filters),
    // La API devuelve { items: [...] } — se extrae .items para que los
    // consumidores del hook reciban SubscriptionOverview[] directamente.
    queryFn: () =>
      api
        .get<{ items: SubscriptionOverview[] }>("/api/v1/internal/subscriptions", {
          query: filters as Record<string, string | undefined>,
        })
        .then((r) => r.items),
    // Como en el panel del tenant: el pago se hace en otra pestaña (Stripe). Al volver se recarga siempre y,
    // mientras alguna suscripción espere un pago, se consulta cada pocos segundos.
    refetchOnWindowFocus: "always",
    refetchInterval: (query) => subscriptionsPollInterval(query.state.data),
  });
}

export function useChangeTenantBillingPlan() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, billingPlanId }: { tenantId: string; billingPlanId: string }) =>
      api.patch<{ id: string; billingPlanId: string; status: string }>(
        `/api/v1/internal/tenants/${tenantId}/billing-plan`,
        { billingPlanId },
      ),
    onSuccess: async (_result, variables) => {
      await client.invalidateQueries({ queryKey: queryKeys.tenants });
      await client.invalidateQueries({ queryKey: ["tenants", "detail", variables.tenantId] });
      await client.invalidateQueries({ queryKey: queryKeys.subscriptions() });
    },
  });
}

export function useDeleteTenant() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      api.delete(`/api/v1/internal/tenants/${tenantId}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.tenants });
      await client.invalidateQueries({ queryKey: queryKeys.subscriptions() });
    },
  });
}

export function useRemoveTenantBillingPlan() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      api.delete(`/api/v1/internal/tenants/${tenantId}/billing-plan`),
    onSuccess: async (_result, tenantId) => {
      await client.invalidateQueries({ queryKey: queryKeys.tenants });
      await client.invalidateQueries({ queryKey: ["tenants", "detail", tenantId] });
      await client.invalidateQueries({ queryKey: queryKeys.subscriptions() });
    },
  });
}

export function useMarkInvoicePaidManually() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<{ invoice: { id: string; status: string }; paymentId: string }>(
        `/api/v1/internal/invoices/${invoiceId}/mark-paid`,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.subscriptions() });
    },
  });
}

// ==================== Módulo financiero: tenant-facing ================
//
// SKILL.md §7: las rutas /subscription, /invoices y /billing/checkout NO
// tienen assertTenantCanOperate — el tenant BLOQUEADO puede acceder a ellas
// para pagar y desbloquearse. Los hooks los usan desde FinancialPage.tsx.

export interface TenantSubscription {
  id: string;
  tenantId: string;
  billingPlanId: string;
  billingPlanName: string;
  periodicity: string;
  status: string;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  autoRenew: boolean;
  hasPaymentMethodOnFile: boolean;
  blockedAt: string | null;
}

export interface TenantInvoice {
  id: string;
  subscriptionId: string;
  periodStart: string;
  periodEnd: string;
  /** Neto (sin impuesto). */
  netAmount: string;
  /** Impuesto añadido (0 si el régimen del tenant no lleva IVA). */
  taxAmount: string;
  /** Lo que se cobra: neto + impuesto. */
  totalAmount: string;
  currency: string;
  status: string;
  dueDate: string;
  paidAt: string | null;
  pdfS3Key: string | null;
  /** Hay factura en el proveedor de pagos: se puede ver y descargar. */
  hasProviderInvoice: boolean;
  /** Factura normal o rectificativa (anulación). */
  kind: "INVOICE" | "RECTIFICATIVE";
  /** Referencia fiscal (`6-000001`) si ya está numerada. */
  fiscalReference: string | null;
  /** Ya tiene número fiscal: existe la factura fiscal en PDF. */
  hasFiscalInvoice: boolean;
}

export interface InvoiceLinks {
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
  number: string | null;
}

export interface CheckoutSessionResult {
  sessionId: string;
  url: string;
}

export function useTenantSubscription() {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.tenantSubscription(activeTenantId),
    queryFn: () => api.get<TenantSubscription>("/api/v1/internal/subscription"),
    retry: false,
    // El pago se hace en otra pestaña (Stripe): al volver se recarga siempre, y mientras la suscripción
    // espere un pago se consulta cada pocos segundos hasta que el aviso de Stripe llegue al servidor.
    refetchOnWindowFocus: "always",
    refetchInterval: (query) => subscriptionPollInterval(query.state.data),
  });
}

export function useTenantInvoices(filters: { page?: number; pageSize?: number } = {}) {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.tenantInvoices(activeTenantId, filters),
    queryFn: () =>
      api.get<{ items: TenantInvoice[]; total: number; page: number; pageSize: number }>(
        "/api/v1/internal/invoices",
        { query: { page: String(filters.page ?? 1), pageSize: String(filters.pageSize ?? 20) } },
      ),
    refetchOnWindowFocus: "always",
    refetchInterval: (query) => invoicesPollInterval(query.state.data?.items),
  });
}

/** Pide al servidor los enlaces (ver / descargar PDF) de una factura. Se piden al pulsar: pueden caducar. */
export function useInvoiceLinks() {
  const api = useApi();
  return useMutation({
    mutationFn: (invoiceId: string) => api.get<InvoiceLinks>(`/api/v1/internal/invoices/${invoiceId}/links`),
  });
}

export function useCreateCheckoutSession() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      mode: "setup" | "payment";
      invoiceId?: string;
      paymentMethodTypes?: ("card" | "sepa_debit" | "bizum")[];
    }) => api.post<CheckoutSessionResult>("/api/v1/internal/billing/checkout", input),
    onSuccess: async () => {
      // Tras una sesión de checkout, invalidamos suscripción e facturas por si
      // el pago ya se procesó vía webhook antes de que el usuario vuelva.
      // Prefijo base: invalida independientemente del tenantId en la queryKey.
      await client.invalidateQueries({ queryKey: ["tenant-subscription"] });
      await client.invalidateQueries({ queryKey: ["tenant-invoices"] });
    },
  });
}

// ------------------------------------------------------------
// Correo de la plataforma — estado y prueba de envío (solo super-admin)
// ------------------------------------------------------------

export interface EmailStatus {
  configured: boolean;
  host?: string;
  port?: number;
  secure?: boolean;
  fromName?: string;
  fromAddress?: string;
}

export interface EmailTestResult {
  ok: boolean;
  configured: boolean;
  message: string;
  code?: string;
  sentAt?: string;
}

export interface TrialNoticePreviewItem {
  tenantId: string;
  tenantName: string;
  kind: "DAYS_7" | "DAYS_1" | "EXPIRED";
  trialEndsAt: string;
  daysLeft: number;
  alreadySent: boolean;
  recipients: string[];
  subject: string;
}

export interface TrialNoticePreview {
  emailConfigured: boolean;
  skippedExpiredLongAgo: number;
  items: TrialNoticePreviewItem[];
}

export function useSendTrialNoticeSample() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { to: string; kind: "DAYS_7" | "DAYS_1" | "EXPIRED" }) =>
      api.post<EmailTestResult>("/api/v1/internal/trial-notices/sample", input),
  });
}

export function useSendTrialSummarySample() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { to: string }) => api.post<EmailTestResult>("/api/v1/internal/trial-summary/sample", input),
  });
}

export function useTrialNoticePreview(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ["trial-notice-preview"] as const,
    queryFn: () => api.get<TrialNoticePreview>("/api/v1/internal/trial-notices/preview"),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export function useEmailStatus(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ["email-status"] as const,
    queryFn: () => api.get<EmailStatus>("/api/v1/internal/email/status"),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

// ------------------------------------------------------------
// SMTP propio del tenant (solo ADMIN)
// ------------------------------------------------------------

export interface TenantSmtpView {
  configured: boolean;
  encryptionKeyConfigured: boolean;
  host?: string;
  port?: number;
  secure?: boolean;
  username?: string;
  fromAddress?: string;
  fromName?: string | null;
  hasPassword?: boolean;
  enabled?: boolean;
  lastTestAt?: string | null;
  lastTestOk?: boolean | null;
  lastTestError?: string | null;
  lastFailureAt?: string | null;
  lastFailureError?: string | null;
}

export interface TenantSmtpInput {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password?: string;
  fromAddress: string;
  fromName?: string;
}

const TENANT_SMTP_KEY = ["tenant-smtp"] as const;

export function useTenantSmtp(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: TENANT_SMTP_KEY,
    queryFn: () => api.get<TenantSmtpView>("/api/v1/internal/tenant-smtp"),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export function useSaveTenantSmtp() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSmtpInput) => api.put<TenantSmtpView>("/api/v1/internal/tenant-smtp", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: TENANT_SMTP_KEY });
    },
  });
}

export function useDeleteTenantSmtp() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>("/api/v1/internal/tenant-smtp"),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: TENANT_SMTP_KEY });
    },
  });
}

export function useTestTenantSmtp() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { to: string }) => api.post<EmailTestResult>("/api/v1/internal/tenant-smtp/test", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: TENANT_SMTP_KEY });
    },
  });
}

export function useSetTenantSmtpEnabled() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { enabled: boolean }) =>
      api.post<TenantSmtpView>("/api/v1/internal/tenant-smtp/enabled", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: TENANT_SMTP_KEY });
    },
  });
}

// ------------------------------------------------------------
// Correo de facturación del tenant (ADMIN)
// ------------------------------------------------------------

export interface TenantBillingEmailView {
  /** Correo de facturación propio; null si no tiene (se usa el del administrador). */
  billingEmail: string | null;
  /** Correo del primer administrador activo (respaldo). */
  adminEmail: string | null;
  /** El que ve el proveedor de pagos y al que llegan facturas y recibos. */
  effectiveEmail: string | null;
}

const TENANT_BILLING_EMAIL_KEY = ["tenant-billing-email"] as const;

export function useTenantBillingEmail(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: TENANT_BILLING_EMAIL_KEY,
    queryFn: () => api.get<TenantBillingEmailView>("/api/v1/internal/tenant-billing-email"),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export function useSaveTenantBillingEmail() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { billingEmail: string | null }) =>
      api.put<TenantBillingEmailView>("/api/v1/internal/tenant-billing-email", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: TENANT_BILLING_EMAIL_KEY });
    },
  });
}

export function useSendTestEmail() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { to: string }) => api.post<EmailTestResult>("/api/v1/internal/email/test", input),
  });
}

// ------------------------------------------------------------
// Zona B — Perfil y contraseña propios del usuario
// ------------------------------------------------------------

export function useUpdateOwnProfile() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { fullName?: string; email?: string }) =>
      api.patch<{ id: string; email: string; fullName: string }>("/api/v1/internal/me/profile", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useUploadAvatar() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { contentType: string; base64: string }) =>
      api.post<{ avatarUrl: string }>("/api/v1/internal/me/avatar", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useDeleteAvatar() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>("/api/v1/internal/me/avatar"),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useChangeOwnPassword() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.patch<void>("/api/v1/internal/me/password", input),
  });
}

// ------------------------------------------------------------
// Zona B — Notificaciones
// ------------------------------------------------------------

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
  decaDocumentId: string | null;
}

export function useNotifications() {
  const api = useApi();
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: queryKeys.notifications(activeTenantId),
    queryFn: () => api.get<{ items: AppNotification[] }>("/api/v1/internal/notifications"),
    // Refrescar cada 60 segundos sin intervención del usuario
    refetchInterval: 60_000,
    select: (data) => data.items,
  });
}

export function useMarkNotificationRead() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<void>(`/api/v1/internal/notifications/${id}/read`, {}),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteAllNotifications() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: number }>("/api/v1/internal/notifications"),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ── Gestión del trial (solo super-admin) ─────────────────────────────────

export interface PatchTrialInput {
  trialEndsAt: string | null; // ISO date string o null para activar ya
}

export function usePatchTenantTrial(tenantId: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PatchTrialInput) =>
      api.patch<void>(`/api/v1/internal/tenants/${tenantId}/trial`, input),
    onSuccess: async () => {
      // Refrescar la vista global de suscripciones y el detalle del tenant
      await client.invalidateQueries({ queryKey: queryKeys.subscriptions() });
      await client.invalidateQueries({ queryKey: ["tenants", "detail", tenantId] });
    },
  });
}

export type { DecaStatus, UseQueryOptions };

// ── Almacenes S3 (solo super-admin) ─────────────────────────────────────────

export type ObjectLockMode = "NONE" | "S3_PER_OBJECT" | "BUCKET_RULE";

export interface S3Config {
  id: string;
  name: string;
  endpoint: string | null;
  region: string;
  bucket: string;
  isDefault: boolean;
  objectLockMode: ObjectLockMode;
  hasVersioning: boolean;
  createdAt: string;
  updatedAt: string;
  _count: { tenants: number };
}

export interface CreateS3ConfigInput {
  name: string;
  endpoint?: string | null;
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  objectLockMode: ObjectLockMode;
  hasVersioning: boolean;
}

export interface UpdateS3ConfigInput {
  name?: string;
  endpoint?: string | null;
  region?: string;
  bucket?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  objectLockMode?: ObjectLockMode;
  hasVersioning?: boolean;
}

export function useS3Configs() {
  const api = useApi();
  return useQuery({
    queryKey: ["s3-configs"],
    queryFn: () => api.get<{ items: S3Config[] }>("/api/v1/internal/s3-configs").then((r) => r.items),
  });
}

export function useCreateS3Config() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateS3ConfigInput) =>
      api.post<S3Config>("/api/v1/internal/s3-configs", input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["s3-configs"] });
    },
  });
}

export function useUpdateS3Config(id: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateS3ConfigInput) =>
      api.patch<S3Config>(`/api/v1/internal/s3-configs/${id}`, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["s3-configs"] });
    },
  });
}

export function useDeleteS3Config() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/internal/s3-configs/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["s3-configs"] });
    },
  });
}

export interface S3TestResult {
  ok: boolean;
  message: string;
}

export function useTestS3Connection() {
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<S3TestResult>(`/api/v1/internal/s3-configs/${id}/test`, {}),
  });
}

// ---------------------------------------------------------------------------
// Migraciones S3 (Fase C)
// ---------------------------------------------------------------------------

export interface S3MigrationSource {
  id: string;
  name: string;
  bucket: string;
}

export interface S3Migration {
  id: string;
  sourceId: string;
  source: S3MigrationSource;
  destinationId: string;
  destination: S3MigrationSource;
  tenantId: string | null;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED" | "CANCELLED";
  totalObjects: number | null;
  copiedObjects: number;
  skippedObjects: number;
  errorCount: number;
  deleteSource: boolean;
  log: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  createdByUserId: string;
}

export interface S3CompatResult {
  ok: boolean;
  sourceReachable: boolean;
  destinationReachable: boolean;
  estimatedObjects: number | null;
  message: string;
}

export interface CreateS3MigrationInput {
  sourceId: string;
  destinationId: string;
  tenantId?: string | null;
  deleteSource?: boolean;
}

/** Listar todas las migraciones S3 (polling manual con refetchInterval) */
export function useS3Migrations() {
  const api = useApi();
  return useQuery({
    queryKey: ["s3-migrations"],
    queryFn: () => api.get<S3Migration[]>("/api/v1/internal/s3-migrations"),
    refetchInterval: 5_000, // polling cada 5 s mientras hay migraciones activas
  });
}

/** Obtener estado de una migración concreta (polling cada 3 s) */
export function useS3MigrationStatus(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["s3-migration", id],
    queryFn: () => api.get<S3Migration>(`/api/v1/internal/s3-migrations/${id}`),
    enabled: Boolean(id),
    refetchInterval: 3_000,
  });
}

/** Test de compatibilidad previo a crear migración */
export function useTestS3Compat() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { sourceId: string; destinationId: string; tenantId?: string | null }) =>
      api.post<S3CompatResult>("/api/v1/internal/s3-migrations/test-compat", input),
  });
}

/** Crear y encolar una migración */
export function useCreateS3Migration() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateS3MigrationInput) =>
      api.post<S3Migration>("/api/v1/internal/s3-migrations", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["s3-migrations"] });
    },
  });
}

// ------------------------------------------------------------
// Exportación al ERP (super-admin)
// ------------------------------------------------------------

export interface ErpExportProblem {
  invoiceId: string;
  invoiceRef: string;
  tenantName: string;
  field: string;
  message: string;
}

export interface ErpExportPreview {
  from: string;
  to: string;
  invoiceCount: number;
  totalAmount: number;
  problems: ErpExportProblem[];
  gap: { count: number; oldestFiscalDate: string | null; oldestReference: string | null };
  alreadyExportedInRange: number;
  canExport: boolean;
}

export interface ErpExportBatch {
  id: string;
  isReexport: boolean;
  originalBatchId: string | null;
  from: string;
  to: string;
  invoiceCount: number;
  /** Facturas que existen ahora enlazadas al lote (puede ser menos que `invoiceCount`). */
  invoicesAvailable: number;
  totalAmount: number;
  createdByUserId: string | null;
  createdAt: string;
}

export interface ErpInvoiceRow {
  id: string;
  reference: string;
  seriesCode: string;
  number: number;
  kind: "INVOICE" | "RECTIFICATIVE";
  fiscalYear: number | null;
  fiscalDate: string | null;
  tenantId: string;
  tenantName: string;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  taxPercent: number | null;
  providerNumber: string | null;
  providerInvoiceId: string | null;
  testMode: boolean;
  exported: boolean;
  exportedAt: string | null;
  exportBatchId: string | null;
  rectifiesInvoiceId: string | null;
  /** Solo en rectificativas: referencia (`6-000001`) de la factura que anulan. */
  rectifiesReference: string | null;
  /** Solo en facturas: la rectificativa que las anula, si existe. */
  rectifiedByInvoiceId: string | null;
  rectifiedByReference: string | null;
}

export interface ErpInvoiceList {
  invoices: ErpInvoiceRow[];
  total: number;
  totalAmount: number;
  limit: number;
  offset: number;
}

export interface ErpInvoiceFilters {
  from: string;
  to: string;
  tenantId: string;
  batchId: string;
  exported: "all" | "pending" | "exported";
  test: "include" | "exclude" | "only";
  limit: number;
  offset: number;
}

export interface ErpExportStatus {
  pendingNumbering: number;
  pendingExport: number;
  numberingBlocked: {
    at: string;
    reason: string;
    detail: string | null;
    invoiceId: string;
    invoiceRef: string;
    tenantName: string;
  } | null;
  finalizationFailures: Array<{
    at: string;
    tenantName: string;
    providerInvoiceId: string | null;
    message: string | null;
  }>;
}

export type ErpConversionKind = "ARTICLE" | "PAYMENT_METHOD" | "SETTING";

export interface ErpConversionItem {
  kind: ErpConversionKind;
  key: string;
  erpCode: string;
}

export interface NumberingCounter {
  fiscalYear: number;
  lastNumber: number;
  nextNumber: number;
  minNextNumber: number;
}

export interface NumberingSeries {
  code: string;
  numberDigits: number;
  maxNumber: number;
  counters: NumberingCounter[];
}

export type VerifactuQrMode = "OFF" | "TEST" | "PRODUCTION";

export interface NumberingSettings {
  configured: boolean;
  numberingDelayDays: number;
  numberTestInvoices: boolean;
  verifactuQrMode: VerifactuQrMode;
  currentFiscalYear: number;
  invoiceSeries: NumberingSeries | null;
  rectificativeSeries: NumberingSeries | null;
}

export interface NumberingSettingsPatch {
  numberingDelayDays?: number;
  numberTestInvoices?: boolean;
  verifactuQrMode?: VerifactuQrMode;
  invoiceSeriesCode?: string;
  rectificativeSeriesCode?: string;
  nextNumber?: { kind: "INVOICE" | "RECTIFICATIVE"; fiscalYear: number; number: number };
}

export interface NumberNowResult {
  numbered: number;
  dropped: number;
  stoppedAt: { invoiceId: string; reason: string; detail?: string } | null;
}

const erpKeys = {
  status: ["erp-export", "status"] as const,
  preview: (from: string, to: string) => ["erp-export", "preview", from, to] as const,
  batches: ["erp-export", "batches"] as const,
  conversions: ["erp-conversions"] as const,
  settings: ["billing-settings"] as const,
};

export function useErpExportStatus() {
  const api = useApi();
  return useQuery({
    queryKey: erpKeys.status,
    queryFn: () => api.get<ErpExportStatus>("/api/v1/internal/erp-export/status"),
  });
}

/** Vista previa del rango. Solo consulta cuando hay un rango (el botón «Comprobar» lo fija). */
export function useErpExportPreview(range: { from: string; to: string } | null) {
  const api = useApi();
  return useQuery({
    queryKey: erpKeys.preview(range?.from ?? "", range?.to ?? ""),
    enabled: range !== null,
    // La vista previa refleja el estado actual: nunca se sirve una copia vieja.
    staleTime: 0,
    gcTime: 0,
    queryFn: () =>
      api.get<ErpExportPreview>("/api/v1/internal/erp-export/preview", {
        query: { from: range!.from, to: range!.to },
      }),
  });
}

/** Facturas numeradas con sus filtros. Cuelga de `["erp-export"]`: exportar o numerar la refresca. */
export function useErpInvoices(filters: ErpInvoiceFilters) {
  const api = useApi();
  return useQuery({
    queryKey: ["erp-export", "invoices", filters] as const,
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<ErpInvoiceList>("/api/v1/internal/erp-export/invoices", {
        query: {
          from: filters.from || undefined,
          to: filters.to || undefined,
          tenantId: filters.tenantId || undefined,
          batchId: filters.batchId || undefined,
          exported: filters.exported,
          test: filters.test,
          limit: filters.limit,
          offset: filters.offset,
        },
      }),
  });
}

export function useErpExportBatches() {
  const api = useApi();
  return useQuery({
    queryKey: erpKeys.batches,
    queryFn: () =>
      api.get<{ batches: ErpExportBatch[] }>("/api/v1/internal/erp-export/batches").then((r) => r.batches),
  });
}

function useInvalidateErp() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["erp-export"] });
  };
}

export function useCreateErpExport() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (input: { from: string; to: string; allowGap: boolean }) =>
      api.post<ErpExportBatch>("/api/v1/internal/erp-export", input),
    onSuccess: invalidate,
  });
}

export function useReexportBatch() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (batchId: string) =>
      api.post<ErpExportBatch>(`/api/v1/internal/erp-export/batches/${batchId}/reexport`, {}),
    onSuccess: invalidate,
  });
}

/** Descarga el ZIP de un lote (FAC + LFA + LCO en XLSX y CSV). */
export function useDownloadErpBatch() {
  const api = useApi();
  return useMutation({
    mutationFn: async (batchId: string) => {
      const blob = await api.get<Blob>(`/api/v1/internal/erp-export/batches/${batchId}/download`, {
        responseType: "blob",
      });
      return blob;
    },
  });
}

export interface ErpBatchDeletion {
  deleted: number;
  invoicesKept: number;
}

/** Borra un lote del historial (y sus reexportaciones si es el original). Las facturas conservan número y marca. */
export function useDeleteErpBatch() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (batchId: string) => api.delete<ErpBatchDeletion>(`/api/v1/internal/erp-export/batches/${batchId}`),
    onSuccess: invalidate,
  });
}

/** Borra del historial los lotes creados antes del día indicado (YYYY-MM-DD). */
export function useDeleteErpBatchesBefore() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (before: string) =>
      api.delete<ErpBatchDeletion>("/api/v1/internal/erp-export/batches", { query: { before } }),
    onSuccess: invalidate,
  });
}

/** ZIP de las facturas ya exportadas de un rango de fechas fiscales, sin pasar por ningún lote. */
export function useDownloadErpRange() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (range: { from: string; to: string }) =>
      api.get<Blob>("/api/v1/internal/erp-export/download", { query: range, responseType: "blob" }),
    onSuccess: invalidate,
  });
}

export function useErpConversions() {
  const api = useApi();
  return useQuery({
    queryKey: erpKeys.conversions,
    queryFn: () =>
      api.get<{ items: ErpConversionItem[] }>("/api/v1/internal/erp-conversions").then((r) => r.items),
  });
}

export function useSaveErpConversions() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (items: ErpConversionItem[]) =>
      api.put<{ items: ErpConversionItem[] }>("/api/v1/internal/erp-conversions", { items }),
    onSuccess: async (data) => {
      client.setQueryData(erpKeys.conversions, data.items);
      // Cambiar un código puede resolver (o crear) problemas de la vista previa.
      await client.invalidateQueries({ queryKey: ["erp-export"] });
    },
  });
}

export function useNumberingSettings() {
  const api = useApi();
  return useQuery({
    queryKey: erpKeys.settings,
    queryFn: () => api.get<NumberingSettings>("/api/v1/internal/billing-settings"),
  });
}

export function useUpdateNumberingSettings() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: NumberingSettingsPatch) =>
      api.put<NumberingSettings>("/api/v1/internal/billing-settings", patch),
    onSuccess: (data) => {
      client.setQueryData(erpKeys.settings, data);
    },
  });
}

export function useNumberNow() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (input: { ignoreDelay: boolean }) =>
      api.post<NumberNowResult>("/api/v1/internal/invoice-numbering/run", input),
    onSuccess: invalidate,
  });
}

/** Crea la rectificativa (anulación completa) de una factura numerada. No toca Stripe. */
export function useRectifyInvoice() {
  const api = useApi();
  const invalidate = useInvalidateErp();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<ErpInvoiceRow>(`/api/v1/internal/erp-export/invoices/${invoiceId}/rectify`, {}),
    onSuccess: invalidate,
  });
}

/** Descarga el PDF de la factura fiscal de cualquier tenant (super-admin). */
export function useErpInvoicePdf() {
  const api = useApi();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.get<Blob>(`/api/v1/internal/erp-export/invoices/${invoiceId}/pdf`, { responseType: "blob" }),
  });
}

/** Descarga el PDF de la factura fiscal del tenant activo. */
export function useFiscalInvoicePdf() {
  const api = useApi();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.get<Blob>(`/api/v1/internal/invoices/${invoiceId}/fiscal-pdf`, { responseType: "blob" }),
  });
}

// ------------------------------------------------------------
// Plantilla de factura (solo super-admin): datos del emisor, color, logo y textos. Global + una por tenant.
// ------------------------------------------------------------

export const INVOICE_TEMPLATE_GLOBAL = "global";

export interface InvoiceTemplateFields {
  primaryColorHex: string | null;
  issuerName: string | null;
  issuerNif: string | null;
  issuerAddress: string | null;
  issuerPostalCode: string | null;
  issuerCity: string | null;
  issuerProvince: string | null;
  issuerCountry: string | null;
  issuerEmail: string | null;
  issuerPhone: string | null;
  issuerWeb: string | null;
  issuerIban: string | null;
  footerText: string | null;
  notesText: string | null;
}

export type InvoiceTemplateLogoSource = "tenant" | "global" | "platform" | "none";

export interface InvoiceTemplateView {
  scope: string;
  tenantName: string | null;
  exists: boolean;
  own: InvoiceTemplateFields;
  inherited: InvoiceTemplateFields;
  effective: InvoiceTemplateFields;
  ownLogoDataUrl: string | null;
  effectiveLogoDataUrl: string | null;
  effectiveLogoSource: InvoiceTemplateLogoSource;
  missingIssuerFields: Array<{ key: string; label: string }>;
}

const invoiceTemplateKey = (scope: string) => ["invoice-template", scope] as const;

export function useInvoiceTemplate(scope: string) {
  const api = useApi();
  return useQuery({
    queryKey: invoiceTemplateKey(scope),
    queryFn: () => api.get<InvoiceTemplateView>(`/api/v1/internal/invoice-templates/${scope}`),
    retry: false,
  });
}

/** Ids de los tenants con plantilla propia. */
export function useInvoiceTemplateOverrides() {
  const api = useApi();
  return useQuery({
    queryKey: ["invoice-template-overrides"] as const,
    queryFn: () => api.get<{ tenantIds: string[] }>("/api/v1/internal/invoice-templates"),
    retry: false,
  });
}

function useInvalidateInvoiceTemplates() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["invoice-template"] });
    await client.invalidateQueries({ queryKey: ["invoice-template-overrides"] });
  };
}

export function useSaveInvoiceTemplate() {
  const api = useApi();
  const invalidate = useInvalidateInvoiceTemplates();
  return useMutation({
    mutationFn: (input: { scope: string; fields: Partial<InvoiceTemplateFields> }) =>
      api.put<InvoiceTemplateView>(`/api/v1/internal/invoice-templates/${input.scope}`, input.fields),
    onSuccess: invalidate,
  });
}

/** Quita la plantilla propia de un tenant: vuelve a usar la global. */
export function useDeleteTenantInvoiceTemplate() {
  const api = useApi();
  const invalidate = useInvalidateInvoiceTemplates();
  return useMutation({
    mutationFn: (scope: string) => api.delete<InvoiceTemplateView>(`/api/v1/internal/invoice-templates/${scope}`),
    onSuccess: invalidate,
  });
}

export function useUploadInvoiceTemplateLogo() {
  const api = useApi();
  const invalidate = useInvalidateInvoiceTemplates();
  return useMutation({
    mutationFn: (input: { scope: string; filename: string; contentType: string; base64: string }) =>
      api.post<{ scope: string; sizeBytes: number }>("/api/v1/internal/invoice-templates/logo", input),
    onSuccess: invalidate,
  });
}

export function useRemoveInvoiceTemplateLogo() {
  const api = useApi();
  const invalidate = useInvalidateInvoiceTemplates();
  return useMutation({
    mutationFn: (scope: string) => api.delete<InvoiceTemplateView>(`/api/v1/internal/invoice-templates/${scope}/logo`),
    onSuccess: invalidate,
  });
}

/** PDF de ejemplo con el borrador del formulario (no guarda nada). */
export function useInvoiceTemplatePreview() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { scope: string; draft?: Partial<InvoiceTemplateFields>; draftWithoutLogo?: boolean }) =>
      api.post<Blob>("/api/v1/internal/invoice-templates/preview", input, { responseType: "blob" }),
  });
}

export interface RefundWarning {
  invoiceId: string;
  reference: string;
  tenantName: string;
  fiscalDate: string | null;
  totalAmount: number;
  amountRefunded: number;
  providerStatus: string;
  total: boolean;
  testMode: boolean;
}

export interface RefundWarningResult {
  configured: boolean;
  checked: number;
  failed: number;
  warnings: RefundWarning[];
}

/** Facturas numeradas que Stripe da por devueltas y aún no tienen rectificativa. */
export function useRefundWarnings() {
  const api = useApi();
  return useQuery({
    queryKey: ["erp-export", "refund-warnings"],
    queryFn: () => api.get<RefundWarningResult>("/api/v1/internal/erp-export/refund-warnings"),
    staleTime: 60_000,
    retry: false,
  });
}
